import { Inject, Injectable, Provider } from '@angular/core'
import { FormlyFieldConfig } from '@ngx-formly/core'
import { TranslateService } from '@ngx-translate/core'
import { forkJoin, map, merge, Observable, tap } from 'rxjs'

import { FormlySelectOption } from '../pipes/select-options.pipe'

@Injectable()
export class FormlyTranslationService {
  public constructor(
    @Inject('prefix') private readonly namespace: string,
    private readonly translateService: TranslateService
  ) {}

  public translateField(
    field: FormlyFieldConfig,
    prefix?: string
  ): Observable<unknown> {
    return merge(
      ...['label', 'placeholder'].map((attributeName) =>
        this.translateAttribute(field, attributeName, attributeName, prefix)
      )
    )
  }

  public translateAttribute(
    field: FormlyFieldConfig,
    key: string,
    attribute: string = key,
    prefix?: string
  ): Observable<unknown> {
    prefix = prefix ? '.' + prefix : ''
    return this.translateAttributeByTranslationKey(
      field,
      `${this.namespace}${prefix}.${field.key}.${key}`,
      attribute
    )
  }

  public translateAttributeByTranslationKey(
    field: FormlyFieldConfig,
    key: string,
    attribute: string
  ): Observable<unknown> {
    return this.translateService.stream(key).pipe(
      tap((value) => {
        if (field.props && typeof field.key === 'string') {
          field.props[attribute] = value
        }
      })
    )
  }

  public translateValidationMessage(
    field: FormlyFieldConfig,
    key: string,
    attribute: string,
    prefix?: string
  ): Observable<unknown> {
    prefix = prefix ? '.' + prefix : ''
    return this.translateValidationMessageByTranslationKey(
      field,
      `${this.namespace}${prefix}.${field.key}.${key}`,
      attribute
    )
  }

  public translateValidationMessageByTranslationKey(
    field: FormlyFieldConfig,
    translationKey: string,
    validatorKey: string
  ): Observable<unknown> {
    return this.translateService.stream(translationKey).pipe(
      tap((value: string) => {
        if (typeof field.key === 'string') {
          if (field.validation?.messages) {
            field.validation.messages[validatorKey] = value
          } else if (field.validation) {
            field.validation.messages = {
              [validatorKey]: value,
            }
          } else {
            field.validation = {
              messages: {
                [validatorKey]: value,
              },
            }
          }
        }
      })
    )
  }

  public getTranslatedOptions(
    options: readonly string[],
    key: string,
    translationAsValue: boolean = false
  ): Observable<FormlySelectOption[]> {
    return forkJoin(
      options.map((type) =>
        this.translateService.get(`models.branch.${key}.options.${type}`).pipe(
          map((translation) => ({
            type,
            translation: translation,
          }))
        )
      )
    ).pipe(
      map((translations) =>
        translations.map((translation) => ({
          value: translationAsValue
            ? translation.translation
            : translation.type,
          label: translation.translation,
        }))
      )
    )
  }
}

export function provideFormlyTranslation(prefix: string): Provider {
  return {
    provide: FormlyTranslationService,
    useFactory: (translate: TranslateService) =>
      new FormlyTranslationService(prefix, translate),
    deps: [TranslateService],
  }
}
